// AUTO-SPLIT from the former monolithic index.ts (issue #292).
// ==========================================
// Organization RBAC Types
// ==========================================

export type OrganizationRole = 'owner' | 'admin' | 'member' | 'viewer';


export type Permission =
  | 'org:view_settings'
  | 'org:modify_settings'
  | 'org:delete'
  | 'billing:view'
  | 'billing:manage'
  | 'github:view_token'
  | 'github:manage_token'
  | 'repo:add'
  | 'repo:remove'
  | 'repo:sync'
  | 'members:view'
  | 'members:invite'
  | 'members:remove'
  | 'members:change_role'
  | 'data:view'
  | 'data:view_sensitive'
  | 'data:export'
  | 'alerts:acknowledge'
  | 'alerts:manage_rules'
  | 'activity:view';


// Role to permissions mapping (mirrors backend)
export const ROLE_PERMISSIONS: Record<OrganizationRole, Permission[]> = {
  owner: [
    'org:view_settings', 'org:modify_settings', 'org:delete',
    'billing:view', 'billing:manage',
    'github:view_token', 'github:manage_token',
    'repo:add', 'repo:remove', 'repo:sync',
    'members:view', 'members:invite', 'members:remove', 'members:change_role',
    'data:view', 'data:view_sensitive', 'data:export',
    'alerts:acknowledge', 'alerts:manage_rules',
    'activity:view',
  ],
  admin: [
    'org:view_settings', 'org:modify_settings', 'org:delete',
    'billing:view', 'billing:manage',
    'github:view_token', 'github:manage_token',
    'repo:add', 'repo:remove', 'repo:sync',
    'members:view', 'members:invite', 'members:remove', 'members:change_role',
    'data:view', 'data:view_sensitive', 'data:export',
    'alerts:acknowledge', 'alerts:manage_rules',
    'activity:view',
  ],
  member: [
    'org:view_settings',
    'github:view_token',
    'repo:sync',
    'members:view',
    'data:view', 'data:export',
    'alerts:acknowledge',
  ],
  viewer: [
    'org:view_settings',
    'github:view_token',
    'members:view',
    'data:view',
  ],
};


export function hasPermission(role: OrganizationRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}


export interface OrganizationMember {
  id: string;
  user_id: string;
  organization_id: string;
  role: OrganizationRole;
  github_login?: string | null;
  name?: string;
  avatar_url?: string;
  email?: string;
  invited_by?: string;
  invited_at?: string;
  created_at: string;
}


export interface OrganizationMemberListResponse {
  members: OrganizationMember[];
  total: number;
  page: number;
  page_size: number;
}


export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';


export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  github_username: string;
  role: OrganizationRole;
  invited_by_username?: string;
  status: InvitationStatus;
  expires_at: string;
  created_at: string;
}


export interface OrganizationInvitationListResponse {
  invitations: OrganizationInvitation[];
  total: number;
}


export interface InviteMemberRequest {
  github_username: string;
  role?: OrganizationRole;
}


export interface ChangeRoleRequest {
  role: OrganizationRole;
}


// ==========================================
// Activity Log Types
// ==========================================

export type ActivityType =
  | 'member_invited'
  | 'member_joined'
  | 'member_removed'
  | 'member_role_changed'
  | 'invitation_revoked'
  | 'org_settings_updated'
  | 'org_name_changed'
  | 'repo_added'
  | 'repo_removed'
  | 'repo_sync_triggered'
  | 'token_refreshed'
  | 'subscription_checkout'
  | 'subscription_upgraded'
  | 'subscription_downgraded'
  | 'subscription_canceled'
  | 'subscription_paused'
  | 'subscription_resumed'
  | 'trial_extended'
  | 'alert_rule_created'
  | 'alert_rule_updated'
  | 'alert_rule_deleted'
  | 'alert_acknowledged';


export interface ActivityLog {
  id: string;
  organization_id: string;
  user_id?: string;
  user_github_login?: string | null;
  user_name?: string;
  user_avatar_url?: string;
  activity_type: ActivityType;
  description: string;
  details: Record<string, unknown>;
  ip_address?: string;
  created_at: string;
}


export interface ActivityLogListResponse {
  activities: ActivityLog[];
  total: number;
  page: number;
  page_size: number;
}


export interface ActivityTypeInfo {
  value: ActivityType;
  category: string;
  description: string;
}
